import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * 9020: a dropdown you can type into to filter its options — a small
 * accessible combobox (WAI-ARIA combobox + listbox) styled like a Bootstrap
 * form control.
 *
 *   <SearchableSelect id="b-vessel" value={vesselId} onChange={setVesselId}
 *     options={vessels.map((v) => ({ value: v.id, label: v.name }))} testId="booking-vessel" />
 *
 * Typing filters the options (case-insensitive, anywhere in the label). Arrow
 * keys move through them, Enter picks, Escape closes and restores the choice.
 * The input carries `testId`; each option `${testId}-option`.
 *
 * Validation feedback: the input is wrapped, so a following `.invalid-feedback`
 * needs `d-block` to show.
 */
export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  invalid = false,
  testId,
  noMatchesText = 'No matches',
  className = '',
}) {
  const autoId = useId();
  const inputId = id || `combo-${autoId}`;
  const listId = `${inputId}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value) || null;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  // Keep the highlighted option in range and in view.
  useEffect(() => {
    if (active >= filtered.length) setActive(Math.max(0, filtered.length - 1));
  }, [filtered.length, active]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const openList = () => {
    if (disabled) return;
    setQuery('');
    const at = selected ? options.indexOf(selected) : 0;
    setActive(at >= 0 ? at : 0);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (opt) => {
    if (!opt) return;
    close();
    if (opt.value !== value) onChange(opt.value);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => Math.min(Math.max(i + step, 0), Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'Enter') {
      // Never submit the surrounding form from the combobox.
      e.preventDefault();
      if (open) pick(filtered[active]);
      else openList();
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const activeId = open && filtered[active] ? `${listId}-${active}` : undefined;

  return (
    <div className={`position-relative ${className}`}>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        className={`form-select form-select-sm${invalid ? ' is-invalid' : ''}`}
        value={open ? query : selected?.label || ''}
        placeholder={open && selected ? selected.label : placeholder}
        disabled={disabled}
        onFocus={openList}
        onClick={() => !open && openList()}
        onBlur={close}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        data-testid={testId}
        data-value={value || ''}
      />
      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className="dropdown-menu show w-100 shadow-sm small"
          style={{ maxHeight: 240, overflowY: 'auto' }}
          data-testid={testId ? `${testId}-listbox` : undefined}
        >
          {filtered.length === 0 ? (
            <li className="dropdown-item-text text-muted" data-testid={testId ? `${testId}-empty` : undefined}>
              {noMatchesText}
            </li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={o.value === value}
                data-index={i}
                data-value={o.value}
                className={`dropdown-item${i === active ? ' active' : ''}`}
                style={{ cursor: 'pointer' }}
                // mousedown, not click: picking must happen before the input blurs.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                data-testid={testId ? `${testId}-option` : undefined}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
